import { useMemo } from 'react';
import * as THREE from 'three';
import { Line, Text } from '@react-three/drei';
import { useAppStore } from '../state/store';
import { STATUS_COLORS } from '../themes/colors';

/**
 * Sun Path overlay (BIM-style)
 *
 * Draws an east-west solar arc with cardinal markers (N/E/S/W), the sun disc
 * and a sun-to-origin ray so the light direction stays readable in 3D views.
 * Purely visual: the shadow-casting directional light keeps its fixed position
 * and this overlay communicates it.
 */

const SUN_POSITION: [number, number, number] = [10, 20, 10];
const ARC_RADIUS = 8;

export function SunPathOverlay() {
    const sunPathVisible = useAppStore((s) => s.view.sunPathVisible);

    const { arcPoints } = useMemo(() => {
        const points: THREE.Vector3[] = [];
        // East (+X) to West (-X) arc passing through the sun elevation
        const startAzimuth = Math.atan2(SUN_POSITION[2], SUN_POSITION[0]);
        for (let i = 0; i <= 64; i++) {
            const angle = startAzimuth + (i / 64) * Math.PI;
            points.push(new THREE.Vector3(
                Math.cos(angle) * ARC_RADIUS,
                SUN_POSITION[1] * 0.5 + Math.sin(angle * 0.5) * 0.1 + 4,
                Math.sin(angle) * ARC_RADIUS * 0.35,
            ));
        }
        return { arcPoints: points };
    }, []);

    if (!sunPathVisible) return null;

    const sunPos = new THREE.Vector3(...SUN_POSITION);
    const cardinal = (x: number, z: number, label: string) => (
        <group key={label} position={[x, 0.05, z]}>
            <Text fontSize={0.6} color={STATUS_COLORS.info} anchorX="center" anchorY="middle">
                {label}
            </Text>
        </group>
    );

    return (
        <group>
            {/* Solar arc */}
            <Line points={arcPoints} color={STATUS_COLORS.info} lineWidth={1.5} dashed dashSize={0.5} gapSize={0.25} />

            {/* Sun disc */}
            <mesh position={sunPos}>
                <sphereGeometry args={[0.6, 24, 24]} />
                <meshBasicMaterial color="#ffcc33" />
            </mesh>

            {/* Sun direction ray to origin */}
            <Line
                points={[sunPos, new THREE.Vector3(0, 0, 0)]}
                color="#ffcc33"
                lineWidth={1}
                dashed
                dashSize={0.4}
                gapSize={0.3}
            />

            {/* Cardinal markers on the ground plane */}
            {cardinal(ARC_RADIUS + 1, 0, 'E')}
            {cardinal(-(ARC_RADIUS + 1), 0, 'W')}
            {cardinal(0, ARC_RADIUS + 1, 'S')}
            {cardinal(0, -(ARC_RADIUS + 1), 'N')}
        </group>
    );
}
